import { GetStaticPaths, GetStaticProps } from 'next'

import { SiteLocale } from '../graphql/datoSchema.generated'
import stripUndefined from '../utils/stripUndefined'
import BlockZone, { normalizeTypename } from '../components/BlockZone'
import blocks from '../blocks'
import { blocksGetStaticProps } from '../blocks/index.server'
import notEmpty from '../utils/notEmpty'
import Layout from '../components/Layout'
import { getLayout, Layout as LayoutType } from '../graphql/layout'
import { getSiteConfig, SiteConfig } from '../graphql/siteConfig'
import { getPage, Page } from '../graphql/page'
import { getPagesList } from '../graphql/pagesList'
import {
  get404Slug,
  get500Slug,
  getHomepageSlug,
  getLocales,
} from '../utils/env'
import getAllPages from '../utils/getAllPages'
import PageSeo from '../components/PageSeo'
import { getSiteSettings, SiteSettings } from '../graphql/siteSettings'

export interface StaticProps {
  siteConfig: SiteConfig
  siteSettings: SiteSettings
  layout: LayoutType
  page: Page
  // [typename]: { [propName]: unknown }
  blockData: Record<string, Record<string, unknown>>
  breadcrumbs: { path: string; page?: Page }[]
}

const hardcodedSlugs = [get404Slug(), get500Slug(), 'search']
const getCmsPageSlugs = async () => {
  return (
    (
      await Promise.all(
        getLocales().map(async (locale) => {
          const siteConfig = await getSiteConfig(locale)
          const pagePages = await getAllPages(
            (page) =>
              getPagesList(
                locale,
                [
                  ...hardcodedSlugs,
                  siteConfig.podcastsIndex?.slug,
                  siteConfig.newsIndex?.slug,
                ].filter(notEmpty),
                100,
                page,
              ),
            100,
          )
          const pages = pagePages.map((page) => page.pages).flat()
          return pages.map(({ slug }) => ({
            slug: slug === getHomepageSlug() ? '' : slug,
            locale,
          }))
        }),
      )
    )
      .flat()
      // Remove pages without valid slug (this should never happen since it's required in the CMS)
      .filter(
        (page): page is typeof page & { slug: string } =>
          page.slug !== undefined && page.slug !== null,
      )
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.FEATUREFLAG_STATICPATHS !== 'true') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  return {
    paths: (await getCmsPageSlugs()).map(({ slug, locale }) => ({
      params: { slugs: slug.split('/') },
      locale,
    })),
    fallback: 'blocking',
  }
}

const getBreadcrumbs = async (locale: SiteLocale, slugs: string[] = []) => {
  const breadcrumbs = await Promise.all(
    slugs.map(async (_, index) => {
      const path: string = slugs
        .slice(0, index + 1)
        .join('/')
        .toLowerCase()
      return {
        path,
        page: await getPage(locale as SiteLocale, path),
      }
    }),
  )
  return breadcrumbs.filter((breadcrumb) => breadcrumb.page)
}

export const getStaticProps: GetStaticProps<
  StaticProps,
  { slugs: string | string[] }
> = async ({ params, locale }) => {
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const slugPath = Array.isArray(params?.slugs)
    ? params?.slugs?.join('/').toLowerCase()
    : params?.slugs?.toLowerCase()
  const slug = slugPath ?? ''

  const [siteConfig, layout, page, _site] = await Promise.all([
    getSiteConfig(locale as SiteLocale),
    getLayout(locale as SiteLocale),
    getPage(locale as SiteLocale, slug || getHomepageSlug()),
    getSiteSettings(locale as SiteLocale),
  ])

  if (!page) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  const breadcrumbs =
    params && typeof params.slugs !== 'string'
      ? await getBreadcrumbs(locale as SiteLocale, params.slugs)
      : undefined

  const blockData = (
    await Promise.all(
      page.content.map(async (block) => {
        const typename = normalizeTypename(block.__typename)
        const getStaticProps = blocksGetStaticProps[typename]
        if (!getStaticProps) {
          return undefined
        }

        return [
          typename,
          await getStaticProps({
            locale: locale as SiteLocale,
            block,
          }),
        ] as const
      }),
    )
  )
    .filter(notEmpty)
    .reduce(
      (obj, [typename, props]) => ({
        ...obj,
        [typename]: props,
      }),
      {},
    )

  return {
    props: stripUndefined({
      siteConfig,
      siteSettings: _site,
      layout,
      page,
      blockData,
      breadcrumbs,
    }),
    // TODO: Configure via environment variables
    revalidate: 60,
  }
}

export const getPageTitle = (
  page: Page,
  siteSettings: SiteSettings['globalSeo'],
) => {
  const titleSuffix = siteSettings?.titleSuffix
  const pageTitle =
    page.seo?.title ||
    // A clean fallback despite the hacky solution
    page.content.reduce<string | undefined>((title, section) => {
      if (title) {
        return title
      }

      if (section.__typename === 'CollageHeaderRecord') {
        return section.title
          ? `${section.title} ${section.subTitle}`
          : undefined
      }

      if (section.__typename === 'SimpleHeaderSectionRecord') {
        return section.title || undefined
      }

      return title
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
    }, siteSettings?.fallbackSeo?.title!)

  return titleSuffix ? `${pageTitle} ${titleSuffix}` : pageTitle
}

interface Props extends StaticProps {
  canonical?: boolean
  alternates?: boolean
}

const PageResolver = ({
  breadcrumbs,
  siteConfig,
  siteSettings,
  layout,
  page,
  blockData,
  canonical = true,
  alternates = true,
}: Props) => (
  <Layout
    siteConfig={siteConfig}
    layout={layout}
    breadcrumbs={
      breadcrumbs
        ? [
            {
              key: 'homepage',
              href: '/',
              title: siteConfig.homepageName || 'Home',
            },
            ...breadcrumbs.map(({ path, page }) => ({
              key: `page-${path}`,
              href: `/${path}`,
              title:
                (page && getPageTitle(page, siteSettings.globalSeo)) || '?',
            })),
          ]
        : undefined
    }
  >
    <PageSeo
      page={page}
      seoSettings={siteSettings.globalSeo}
      canonical={canonical === false ? canonical : undefined}
      alternates={alternates}
    />

    <BlockZone components={blocks} content={page.content} data={blockData} />
  </Layout>
)

export default PageResolver
